import { FontSpec } from "chart.js";
import { ChartPlugins, ChartUtils } from "@app/utils/chartUtils";
import { useTheme } from "@mui/material";
import { formatPercentFactor } from "@app/utils";
import { CarPdfChart, CarPdfChartProps } from "@app/components/PdfChart";
import {
  CarPortfolioChartItem,
  CarPortfolioStatPeriod,
} from "@app/pages/assetAllocation/asset-allocation-types";
import { getOutUnderPerformanceChartData } from "@app/pages/assetAllocation/editPortfolioGroup/ChartOutUnderPerformance";
import { CarPdfBox } from "@app/components/PdfBox";
import { CarPdfChartLegend } from "@app/components/PdfChartLegend";

interface OutUnderPerformancePdfChartProps
  extends Omit<CarPdfChartProps, "type" | "options" | "plugins" | "data"> {
  data: CarPortfolioChartItem[];
  periods: CarPortfolioStatPeriod[];
  data1Id: string;
  data2Id: string;
  startYear?: number;
  endYear?: number;
  isRelative: boolean;
  isWhiteContext?: boolean;
}

export const OutUnderPerformancePdfChart = ({
  data,
  periods,
  data1Id,
  data2Id,
  startYear,
  endYear,
  isRelative,
  isWhiteContext,
  ...props
}: OutUnderPerformancePdfChartProps) => {
  const theme = useTheme();

  const { datasets, range, y1Label, y2Label } = getOutUnderPerformanceChartData(
    {
      theme,
      data,
      data1Id,
      data2Id,
      startYear,
      endYear,
      isRelative,
      forPdf: true,
    },
  );

  const font: Partial<FontSpec> = {
    size: 9,
    weight: 400,
    lineHeight: 1,
    family: theme.typography.fontFamily,
  };

  const titleFont: Partial<FontSpec> = {
    ...font,
    size: 11,
    weight: 600,
  };

  return (
    <CarPdfBox>
      <CarPdfChart
        type="line"
        data={{
          datasets,
        }}
        options={{
          scales: {
            x: {
              type: "time",
              ticks: {
                display: false,
              },
              grid: {
                display: false,
              },
              time: {
                unit: "year",
              },
            },
            y: {
              offset: true,
              ticks: {
                font,
                color: theme.palette.softBlack,
                padding: 0,
                callback: (value) =>
                  typeof value === "number"
                    ? formatPercentFactor(value)
                    : value,
              },
              grid: ChartUtils.getGridOptions({
                theme,
                showMinMaxLines: true,
                highlightZero: true,
              }),
              title: {
                display: true,
                text: y1Label,
                color: theme.palette.softBlack,
                font: titleFont,
              },
              min: range.minY,
              max: range.maxY,
            },
            y2: {
              offset: true,
              position: "right",
              grid: {
                display: false,
              },
              ticks: {
                font,
                color: theme.palette.softBlack,
                padding: 0,
                callback: (value) =>
                  typeof value === "number"
                    ? formatPercentFactor(value)
                    : value,
              },
              title: {
                display: true,
                text: y2Label,
                color: theme.palette.softBlack,
                font: titleFont,
              },
              min: range.minY2,
              max: range.maxY2,
            },
          },
          plugins: {
            roundedBackground: ChartUtils.getRoundedBackgroundOptions({
              theme,
              isWhiteContext,
            }),
            periods: {
              backgroundColor: "#EDEEF0", // "rgba(236, 238, 240, 0.5)",
              borderColor: "#E1E2E3", // "rgba(178, 179, 181, 0.5)",
              font: {
                ...font,
                weight: 600,
                size: 9,
              },
              textColor: theme.palette.softBlack,
              items: periods.map((p) => ({
                start: new Date(p.startDate).getTime(),
                end: new Date(p.endDate).getTime(),
                label: p.label,
              })),
            },
            yearTicks: {
              color: theme.palette.gray6,
              font: {
                ...font,
                size: 6,
              },
              marginTop: 16,
            },
          },
        }}
        plugins={[
          ChartPlugins.roundedBackground,
          ChartPlugins.periods,
          ChartPlugins.yearTicks,
        ]}
        {...props}
      />
      <CarPdfChartLegend
        style={{ marginTop: 2 }}
        items={datasets.map((i) => ({
          label: i.label ?? "",
          color: String(i.borderColor),
          isCircle: true,
        }))}
      />
    </CarPdfBox>
  );
};
